// What the forms for blasts, rolled rests and Delay offer (qa-runs#1171 part 3), as the server reads it from the
// system's content (`GET /api/tables/:campaignId/table-extras`, server/src/tableExtras.ts): the delay rule, the ways a
// blast is thrown and its kinds, and the rests that roll. Names and numbers a person reads; every roll is the server's.
// Read once per table, through `authFetch` like every call to this origin.
import { useEffect, useState } from 'react';
import { authFetch } from './session';

export interface DelayView { until: 'pass' | 'own_turn'; takesPlace: boolean; label: string }
export interface BlastView {
  types: Array<{ key: string; label: string }>;
  kinds: Array<{ key: string; label: string; power: number; perMetre: number; level: string; stun: boolean }>;
  bands: string[];
}
export interface RestView { key: string; label: string; restores: string; shape: 'per_box' | 'stage'; extraDice: boolean }
export interface TableExtras { delay: DelayView | null; blast: BlastView | null; rests: RestView[] }

export const NO_EXTRAS: TableExtras = { delay: null, blast: null, rests: [] };
const cache = new Map<string, Promise<TableExtras>>();

/** The answer, checked; anything malformed reads as none. */
export function parseExtras(v: unknown): TableExtras {
  const b = v as Partial<TableExtras> | null;
  if (!b || typeof b !== 'object') return NO_EXTRAS;
  const d = b.delay;
  const delay = d && (d.until === 'pass' || d.until === 'own_turn') && typeof d.takesPlace === 'boolean' && typeof d.label === 'string' ? { until: d.until, takesPlace: d.takesPlace, label: d.label } : null;
  const bl = b.blast;
  const blast = bl && Array.isArray(bl.types) && Array.isArray(bl.kinds) && bl.types.length && bl.kinds.length ? { types: bl.types, kinds: bl.kinds, bands: Array.isArray(bl.bands) ? bl.bands : [] } : null;
  const rests = Array.isArray(b.rests) ? b.rests.filter((r) => r && typeof r.key === 'string' && typeof r.label === 'string') : [];
  return { delay, blast, rests };
}

export function fetchExtras(campaignId: string, fetchImpl: typeof fetch = fetch): Promise<TableExtras> {
  let p = cache.get(campaignId);
  if (!p) {
    p = authFetch(`/api/tables/${encodeURIComponent(campaignId)}/table-extras`, { headers: { Accept: 'application/json' } }, fetchImpl)
      .then(async (r) => (r.ok ? parseExtras(await r.json()) : NO_EXTRAS))
      .catch(() => NO_EXTRAS);
    p.then((x) => { if (x === NO_EXTRAS) cache.delete(campaignId); });
    cache.set(campaignId, p);
  }
  return p;
}
export const forgetExtras = () => cache.clear();

/** The table's extras, none until they arrive. */
export function useTableExtras(campaignId: string | null | undefined): TableExtras {
  const [x, setX] = useState<TableExtras>(NO_EXTRAS);
  useEffect(() => {
    if (!campaignId) return;
    let live = true;
    void fetchExtras(campaignId).then((v) => { if (live) setX(v); });
    return () => { live = false; };
  }, [campaignId]);
  return x;
}
